import { ArrowTopRightOnSquareIcon, ShoppingCartIcon } from '@heroicons/react/20/solid';
import { useFetcher } from '@remix-run/react';
import { clsx } from 'clsx';
import { Button, Spinner } from 'flowbite-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useLocale } from 'remix-i18next/react';
import { useActionUrlWithOrigin } from '#common/hooks/useActionUrlWithOrigin.ts';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { getMediumTitle } from '#common/medium-display';
import { minimalTakeSchema, renderHiddenFieldsFromDiscriminatedUnion } from '#common/models/tts-models';
import { useTranslatedPath } from '#common/utils/i18n.js';
import { useAffiliateRef } from '#common/utils/tracking/affiliate-ref-context.tsx';
import { useVoiceSlug } from '#speaker-shop/hooks/useVoiceSlug';
import { calculatePriceForRecording } from '#speaker-shop/models/price-calculation';
import { trackCheckoutTotalSum, trackClickCheckout } from '#speaker-shop/utils/tracking.ts';
import { Checkbox } from './Checkbox';
import { CouponEntry, type CouponInfo } from './CouponEntry';
import { Feature } from './Feature';
import { useRecordingContext } from './RecordingContext';

interface Props {
    allowedMediums: {
        id: string;
        medium: { name: string; titleEn: string | null; titleDe: string | null };
        basePrice: number;
        basePriceCharacters: number;
    }[];
    currency: string;
}

export function OrderCard({ allowedMediums, currency }: Props) {
    const initiatePurchaseFetcher = useFetcher();
    const locale = useLocale();
    const { t, rawT } = useEditableTranslation('checkout');
    const { recordings } = useRecordingContext();
    const translatePath = useTranslatedPath(locale);
    const [ appliedCoupon, setAppliedCoupon ] = useState<CouponInfo | null>(null);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const initiatePurchaseUrl = useActionUrlWithOrigin('/api/initiate-purchase');
    const voiceSlug = useVoiceSlug();
    const affiliateRef = useAffiliateRef();

    const originalPrice = useMemo(() => {
        if (recordings.length > 0) {
            return allowedMediums.reduce(
                (total, allowedUse) =>
                    total + recordings.reduce((acc, x) => acc + calculatePriceForRecording(x, allowedUse).price, 0),
                0
            );
        }

        return allowedMediums.reduce((acc, allowedUse) => acc + allowedUse.basePrice, 0);
    }, [ allowedMediums, recordings ]);
    const discountedPrice = useMemo(() => {
        if (appliedCoupon) {
            if (appliedCoupon.type === 'percentage') {
                return originalPrice * (1 - appliedCoupon.discount / 100);
            }
        }

        return originalPrice;
    }, [ appliedCoupon, originalPrice ]);
    const [ acceptTerms, setAcceptTerms ] = useState(false);
    const allowOrder = useMemo(
        () =>
            acceptTerms && recordings.filter(x => x.takes.some(t => t.isChecked)).length > 0
            && allowedMediums.length > 0,
        [ acceptTerms, recordings, allowedMediums ]
    );

    useEffect(() => {
        trackCheckoutTotalSum(originalPrice, discountedPrice, appliedCoupon?.code);
    }, [ originalPrice, discountedPrice, appliedCoupon ]);
    useEffect(() => {
        const onPageShow = () => setIsSubmitting(false);
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    const onSubmit = () => {
        setIsSubmitting(true);
        trackClickCheckout(originalPrice, discountedPrice, appliedCoupon?.code);
    };

    useEffect(() => {
        if (initiatePurchaseFetcher.state === 'idle') {
            setIsSubmitting(false);
        }
    }, [ initiatePurchaseFetcher.state ]);

    return (
        <initiatePurchaseFetcher.Form
            method='POST'
            action={initiatePurchaseUrl}
            className='bg-radial-[60%_70%_at_12%_25%] from-crispybluedark-600 to-crispybluedark-700 rounded-4xl px-10 py-12 text-white md:w-88 flex flex-col gap-y-6 sticky top-4 mb-8'
            onSubmit={onSubmit}
        >
            {voiceSlug && <input type='hidden' name='voiceSlug' value={voiceSlug} />}
            {affiliateRef && <input type='hidden' name='ref' value={affiliateRef} />}
            <div>
                {appliedCoupon && (
                    <div className='relative inline-block w-fit line-through'>
                        <span>{currency} {originalPrice.toFixed(2)}</span>
                        {
                            /* <svg className='absolute inset-0 w-full h-full pointer-events-none' preserveAspectRatio='none'>
                            <line x1='0' y1='100%' x2='100%' y2='0' stroke='red' strokeWidth='2' />
                        </svg> */
                        }
                    </div>
                )}
                <h1 className={clsx('flex items-baseline font-bold', appliedCoupon && 'text-green-500')}>
                    <span className='mr-4 mb-1 text-3xl'>{currency}</span>{' '}
                    <span className='text-5xl md:text-6xl'>{discountedPrice.toFixed(2)}</span>
                </h1>
            </div>
            <div className='text-base italic gap-y-3 flex flex-col'>
                <Feature text={t('voice-owner-approved')} />
                <Feature
                    text={
                        <>
                            {t('allowed-medium', { count: allowedMediums.length })}:{' '}
                            {allowedMediums.map((x, i) => (
                                <Fragment key={x.id}>{i > 0 && ', '} {getMediumTitle(x.medium, locale)}</Fragment>
                            ))}
                        </>
                    }
                />
                <Feature text={<>{t('format')}: {t('common:formats.mp3')}, 128 kBit/s</>} />
                <Feature text={t('including-release-document')} />
                <Feature text={t('download-after-payment')} />
            </div>
            <input type='hidden' name='currency' value={currency} />
            {allowedMediums.map((x, i) => <input
                key={x.id}
                type='hidden'
                name={`allowedMediums[${i}]`}
                value={x.id}
            />)}
            {recordings.map((recording, i) =>
                recording.takes.filter(take => take.isChecked).map((take, j) => (
                    <Fragment key={take.takeId}>
                        {renderHiddenFieldsFromDiscriminatedUnion({
                            discriminator: 'voiceType',
                            namePrefix: `recordings[${i}][takes][${j}]`,
                            schema: minimalTakeSchema,
                            value: take
                        })}
                    </Fragment>
                ))
            )}
            <div className='flex flex-col gap-y-4'>
                <CouponEntry onCouponApplied={setAppliedCoupon} />
                <Checkbox
                    label={
                        <span>
                            <Trans t={rawT} i18nKey='accept-terms'>
                                <a href={translatePath('/gtc')} target='_blank' className='underline'></a>
                                <ArrowTopRightOnSquareIcon className='ml-1 inline h-5 w-5' />
                            </Trans>
                        </span>
                    }
                    isChecked={acceptTerms}
                    onChange={setAcceptTerms}
                    name='acceptTerms'
                />
            </div>
            <Button
                className='w-full h-16 rounded text-3xl items-center'
                color='yellow'
                disabled={!allowOrder || isSubmitting}
                type='submit'
            >
                {isSubmitting && <Spinner size='md' className='mr-2' />}
                <ShoppingCartIcon className='inline-block w-6 h-6 mr-2' /> {t('order-now')}
            </Button>
        </initiatePurchaseFetcher.Form>
    );
}
